import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const DISPLAY_LAYOUT_LIST_QUERY = gql`
  query DisplayLayoutList(
    $filter: DisplayLayoutFilter
    $params: PaginationAndSorting
  ) {
    displayLayoutList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        description
        previewUrl
        layoutConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_LAYOUT_GET_QUERY = gql`
  query DisplayLayoutGet($id: ID) {
    displayLayoutGet(id: $id) {
      code
      data {
        id
        name
        description
        previewUrl
        layoutConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_LAYOUT_GET_MANY_QUERY = gql`
  query DisplayLayoutGetMany($ids: [ID]) {
    displayLayoutGetMany(ids: $ids) {
      code
      data {
        id
        name
        description
        previewUrl
        layoutConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const DISPLAY_LAYOUT_CREATE_MUTATION = gql`
  mutation DisplayLayoutCreate($params: DisplayLayoutInput) {
    displayLayoutCreate(params: $params) {
      code
      data {
        id
        name
        description
        previewUrl
        layoutConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_LAYOUT_UPDATE_MUTATION = gql`
  mutation DisplayLayoutUpdate(
    $where: DisplayLayoutWhereInput
    $params: DisplayLayoutInput
  ) {
    displayLayoutUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        description
        previewUrl
        layoutConfig
        rights
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_LAYOUT_REMOVE_MUTATION = gql`
  mutation DisplayLayoutRemove($where: DisplayLayoutWhereInput) {
    displayLayoutRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_LAYOUT_REMOVE_MANY_MUTATION = gql`
  mutation DisplayLayoutRemoveMany($where: [DisplayLayoutWhereInput]) {
    displayLayoutRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface DisplayLayout {
  id: string;
  name?: string;
  description?: string | null;
  layoutConfig?: string | null;
  status?: number;
}

export interface DisplayLayoutFilter {
  id?: string[] | null | undefined;
  name?: string | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface DisplayLayoutInput {
  name?: string | null | undefined;
  description?: string | null | undefined;
  layoutConfig?: string | null | undefined;
  status?: number | null | undefined;
}

export interface DisplayLayoutWhereInput {
  id: string;
}

/**
 * Display Layout service that handles all display layout-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayLayoutService {
  /**
   * Get a list of display layouts with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayLayoutFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_LAYOUT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single display layout by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_LAYOUT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple display layouts by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_LAYOUT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new display layout
   */
  static async create(params: DisplayLayoutInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_LAYOUT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing display layout
   */
  static async update(
    id: string,
    params: DisplayLayoutInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_LAYOUT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a display layout
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_LAYOUT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple display layouts
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      DISPLAY_LAYOUT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
